<template>
  <v-dialog v-model="dialog" width="800" :persistent="true">
    <template v-slot:activator="{ on }">
      <permission-tooltip
          :permission="hasPermission('calendar')"
          :tooltip="{right: true, bottom: false, attach: false}"
      >
        <v-btn
            color="primary"
            class="ml-0 mr-0"
            @click="open"
            :disabled="!hasPermission('calendar')"
        >
          {{ 'app.calendar.add_event' | trans }}
        </v-btn>
      </permission-tooltip>
    </template>
    <v-card>
      <v-toolbar card dark color="primary">
        <v-toolbar-title>{{ showEventDialogTitle() }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn icon dark small @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text style="height: 500px;">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-layout row wrap align-center>
            <v-flex xs12>
              <v-text-field
                  v-model="calendarEvent.name"
                  :label="trans('app.name')"
                  single-line
                  :rules="rules.name"
                  ref="nameInput"
              ></v-text-field>
            </v-flex>
            <v-flex xs6 align-center>
              <v-menu
                  v-model="menu1"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                      id="formatted-start-date-input"
                      v-model="formattedDisplayDate"
                      :label="'app.calendar.event.start_date' | trans"
                      prepend-icon="event"
                      readonly
                      v-on="on"
                      :rules="rules.startDate"
                  ></v-text-field>
                </template>
                <v-date-picker
                    v-model="calendarEvent.startDate"
                    @input="menu1 = false"
                    locale="pl-pl"
                    first-day-of-week="1"
                ></v-date-picker>
              </v-menu>
            </v-flex>
            <v-flex xs4 class="pl-2">
                <v-layout row wrap>
                  <v-flex xs12>
                  <vue-timepicker
                      class="align-center"
                      :minute-interval="5"
                      v-model="calendarEvent.startTime"
                      hide-clear-button
                      input-width="80px"
                      auto-scroll
                      @change="changeStartTime"
                  ></vue-timepicker>
                    <v-text-field
                        v-model="calendarEvent.startTime"
                        :rules="rules.startTime"
                        style="display: none"
                        ref="startTimeInput"
                      />-
                    <vue-timepicker
                        class="align-center"
                        hide-clear-button
                        input-width="80px"
                        :minute-interval="5"
                        v-model="calendarEvent.endTime"
                        :rules="rules.endTime"
                        :hour-range="endTimeHourRange"
                        auto-scroll
                    ></vue-timepicker>
                    <v-text-field
                        v-model="calendarEvent.endTime"
                        :rules="rules.endTime"
                        style="display: none"
                        ref="endTimeInput"
                      />
                  </v-flex>
                </v-layout>
            </v-flex>
            <v-flex xs6></v-flex>
            <v-flex xs6>
              <div class="v-messages theme--light error--text" v-if="timeErrors.length > 0">
                <div class="v-messages__wrapper">
                  <div class="v-messages__message">
                    {{ timeErrors[0] }}
                  </div>
                </div>
              </div>
            </v-flex>
            <v-flex xs12>
              <v-select
                  v-model="calendarEvent.labelId"
                  :items="labels"
                  :label="'app.calendar.label' | trans"
                  item-text="name"
                  item-value="id"
              >
                  <template v-slot:append>
                      <div
                        :class="`color-box ${labelColor} pointer`"
                      ></div>
                  </template>
              </v-select>
            </v-flex>
            <v-flex xs12>
              <v-textarea
                  :label="'app.calendar.additional_information' | trans"
                  :rules="rules.additionalInformation"
                  v-model="calendarEvent.additionalInformation"
                  full
                  rows="10"
              ></v-textarea>
            </v-flex>
          </v-layout>
        </v-form>

      </v-card-text>

      <v-card-actions>
          <v-btn color="error" v-if="calendarEvent.id !== null" @click="deleteCalendarEvent" :loading="deleteLoading">
              {{ 'app.delete' | trans }}
          </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" flat @click="close">
          {{ 'app.close' | trans }}
        </v-btn>
        <v-btn color="primary" @click="save" :disabled="(!valid) || timeErrors.length > 0" :loading="loading">
          {{ 'app.save' | trans }}
        </v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import {trans} from "../../app";
import {mapActions, mapGetters} from "vuex";
import RulesBuilder from "../../utils/rulesBuilder";
import axios from "axios";
import VueTimepicker from 'vue2-timepicker';
import {SYSTEM_DATE} from "../../utils/dateFormatter";

export default {
  components: {
    VueTimepicker,
  },
  data() {
    return {
      dialog: false,
      valid: false,
      loading: false,
      deleteLoading: false,
      menu1: false,
      menu2: false,
      startTimeDialog: false,
      endTimeDialog: false,
      rulesBuilder: new RulesBuilder(),
      calendarEvent: {
        id: null,
        name: '',
        startDate: moment().format(SYSTEM_DATE),
        startTime: '12:00',
        endTime: '13:00',
        labelId: null,
        additionalInformation: '',
      },
      timeErrors: [],
      formattedDisplayDate: moment().format('dddd, D MMMM YYYY'),
    };
  },
  async created() {
    await this.fetchCalendarLabels();
  },
  methods: {
    ...mapActions(["fetchCalendarLabels", "fetchCalendarEvents"]),
    open() {
      this.dialog = true;
    },
    openAndEdit(calendarEvent) {
      this.calendarEvent.id = calendarEvent.id;
      this.calendarEvent.labelId = calendarEvent.label && calendarEvent.label.id;
      this.calendarEvent.name = calendarEvent.name;
      const [startDate, startTime] = calendarEvent.start_date.split(' ');
      const endTime = calendarEvent.end_date.split(' ')[1];
      this.calendarEvent.startDate = startDate;
      this.calendarEvent.startTime = startTime.slice(0, -3);
      this.calendarEvent.endTime = endTime.slice(0, -3);
      this.calendarEvent.additionalInformation = calendarEvent.additional_information;
      this.open();
    },
    close() {
      this.dialog = false;
      this.loading = false;
      const formReset = new Promise((resolve, reject) => {
        this.$refs.form.inputs.forEach(input => {
          if (input['$attrs'].id !== 'formatted-start-date-input') {
            input.reset();
          }
        });
        resolve();
      })

      formReset.then(() => {
        this.calendarEvent = {
          id: null,
          name: '',
          startDate: moment().format(SYSTEM_DATE),
          startTime: '12:00',
          endTime: '13:00',
          labelId: null,
          additionalInformation: '',
        };
      });
    },
    async save() {
      await this.$refs.form.validate();
      if (!this.valid) {
        return;
      }

      this.loading = true;
      const startDate = `${this.calendarEvent.startDate} ${this.calendarEvent.startTime}:00`;
      const endDate = `${this.calendarEvent.startDate} ${this.calendarEvent.endTime}:00`;

      const data = {
        name: this.calendarEvent.name,
        start_date: startDate,
        end_date: endDate,
        label_id: this.calendarEvent.labelId,
        additional_information: this.calendarEvent.additionalInformation || '',
      };

      try {
        //patch
        if (this.calendarEvent.id === null) {
          await axios.post('/v2/calendar-events', data);
          this.$toast.success(trans('app.actions.added', {name: trans('app.event_accusative')}));
        } else {
          await axios.patch(`/v2/calendar-events/${this.calendarEvent.id}`, data);
          this.$toast.success(trans('app.actions.edited', {name: trans('app.event_accusative')}));
        }
        await this.fetchCalendarEvents();
      } catch (e) {
        this.$toast.error(trans('app.error_occurred'));
      }

      this.close();
    },
    async deleteCalendarEvent() {
        const confirm = await this.$confirm(trans('app.calendar.confirm_removal', {name: this.calendarEvent.name}), {
          title: trans('app.remove_event')
        });

        if (false === confirm) {
          return;
        }

        this.deleteLoading = true;
        try {
          await axios.delete(`/v2/calendar-events/${this.calendarEvent.id}`);
          this.$toast.success(trans('app.actions.removed', {name: trans('app.event_accusative')}));
          await this.fetchCalendarEvents();
        } catch (e) {
            this.$toast.error(trans('app.error_occurred'));
        }
        this.deleteLoading = false;
        this.close();
    },
    showEventDialogTitle() {
      return this.calendarEvent.id === null ? trans('app.calendar.add_event') : trans('app.calendar.event_details')
    },
    changeStartTime(eventData) {
      if (this.calendarEvent.id !== null) {
        return;
      }

      const startDate = moment(eventData.displayTime, 'HH:ss');
      const hour = startDate.hour();
      if (hour === 23) {
        this.calendarEvent.endTime = '23:59';

        return;
      }
      this.calendarEvent.endTime = startDate
          .add(60, 'minutes')
          .format("HH:ss");
    },

  },
  computed: {
    ...mapGetters(['calendarLabels', 'calendarEvents', 'hasPermission']),
    labels() {
      return [
        {id: null, name: trans('app.no_data')},
        ...this.calendarLabels
      ];
    },
    rules: function () {
      return {
        name: this.rulesBuilder
            .addRequiredRule(trans('app.name'))
            .addMinLengthRule(trans('app.name'), 1)
            .addMaxLengthRule(trans('app.name'), 255)
            .getRules(),
        startDate: this.rulesBuilder
            .addRequiredRule(trans('app.calendar.event.start_date'))
            .addIsValidDate(trans('app.calendar.event.start_date'), 'dddd, D MMMM YYYY')
            .getRules(),
        startTime: this.rulesBuilder
            .addRequiredRule(trans('app.calendar.event.start_time'))
            .addIsTimeRule(trans('app.calendar.event.start_time'))
            .addTimeIsBefore(this.calendarEvent.endTime)
            .getRules(),
        endTime: this.rulesBuilder
            .addRequiredRule(trans('app.calendar.event.end_time'))
            .addIsTimeRule(trans('app.calendar.event.end_time'))
            .addTimeIsAfter(this.calendarEvent.startTime)
            .getRules(),
        additionalInformation: this.rulesBuilder
            .addMaxLengthRule(trans('app.calendar.additional_information'), 255)
            .getRules(),
      }
    },
    labelColor() {
        if (this.calendarEvent.labelId === null) {
            return '';
        }

        const label = this.labels.find(label => label.id === this.calendarEvent.labelId);

        if (label !== undefined) {
            return label.color;
        }

        return '';
    },
    endTimeHourRange() {
      const startTime = moment(`${this.calendarEvent.startDate} ${this.calendarEvent.startTime || '12:00'}:00`);
      const startHour = startTime.hour();

      if (startHour === 23) {
        return [startHour];
      }

      return [[startHour, 23]];
    },
  },
  updated() {
    let errors = [];
    if (this.$refs.endTimeInput) {
      this.$refs.endTimeInput.validate();
      errors = this.$refs.endTimeInput.errorBucket;
    }

    if (this.$refs.startTimeInput) {
      this.$refs.startTimeInput.validate();
      errors = [
          ...this.$refs.startTimeInput.errorBucket,
          ...errors,
      ];
    }

    const startDate = `${this.calendarEvent.startDate} ${this.calendarEvent.startTime}:00`;

    const eventWithTheSameStartDate = this.calendarEvents.find(calendarEvent => {
      return startDate === calendarEvent.start_date && this.calendarEvent.id !== calendarEvent.id;
    });

    if (eventWithTheSameStartDate !== undefined) {
      errors.push(trans('validation.custom.calendar_events.event_for_date_exists'));
    }

    //if is very important because without it component will fall into infinitive render loop
    if (this.timeErrors.toString() !== errors.toString()) {
      this.timeErrors = errors;
    }
  },
  watch: {
    'calendarEvent.startDate': function () {
      this.formattedDisplayDate = moment(this.calendarEvent.startDate).format('dddd, D MMMM YYYY');
    },
    formattedDisplayDate: function () {
      if (undefined === this.formattedDisplayDate) {
        this.formattedDisplayDate = moment(this.calendarEvent.startDate).format('dddd, D MMMM YYYY');
      }
    },
  },
}
</script>

<style scoped>
  .color-box {
    width: 20px;
    height: 20px;
    border-radius: 3px;
  }

  .yellow {
      background: #f6bf26;
  }

  .orange {
      background: #f4511e;
  }

  .red {
      background: #d50000;
  }

  .violet {
      background: #7986cb;
  }

  .blue {
      background: #039be5;
  }

  .green {
      background: #4caf50;
  }

  .align-center {
    text-align: center;
  }
</style>
